import * as XLSX from 'xlsx-js-style';
import moment from 'moment';

const HEADER_FILL = { fgColor: { rgb: '1A237E' } };
const HEADER_FONT = { bold: true, color: { rgb: 'FFFFFF' }, sz: 11, name: 'Calibri' };
const TITLE_FONT = { bold: true, sz: 16, color: { rgb: '1A237E' }, name: 'Calibri' };
const META_LABEL_FONT = { bold: true, sz: 10, color: { rgb: '424242' }, name: 'Calibri' };
const META_VALUE_FONT = { sz: 10, name: 'Calibri' };
const SECTION_FONT = { bold: true, sz: 12, color: { rgb: '0D47A1' }, name: 'Calibri' };
const TOTAL_FILL = { fgColor: { rgb: 'E3F2FD' } };
const TOTAL_FONT = { bold: true, sz: 10, name: 'Calibri' };
const THIN_BORDER = {
  top: { style: 'thin', color: { rgb: 'BDBDBD' } },
  bottom: { style: 'thin', color: { rgb: 'BDBDBD' } },
  left: { style: 'thin', color: { rgb: 'BDBDBD' } },
  right: { style: 'thin', color: { rgb: 'BDBDBD' } },
};

const moneyNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (value) => {
  if (!value) return '—';
  const m = moment(value);
  return m.isValid() ? m.format('DD MMMM YYYY, dddd') : String(value);
};

const fmtDateTime = (value) => {
  if (!value) return '—';
  const m = moment(value);
  return m.isValid() ? m.format('DD.MM.YYYY HH:mm') : String(value);
};

const safeSheetName = (name) => {
  const raw = String(name || 'Sheet')
    .replace(/[\\/*?:\[\]]/g, ' ')
    .trim()
    .slice(0, 31);
  return raw || 'Sheet';
};

const uniqueSheetName = (wb, name) => {
  const used = new Set(wb.SheetNames);
  let sheetName = safeSheetName(name);
  let i = 1;
  while (used.has(sheetName)) {
    const suffix = ` (${i})`;
    sheetName = safeSheetName(name).slice(0, 31 - suffix.length) + suffix;
    i += 1;
  }
  return sheetName;
};

const styleCell = (ws, r, c, style) => {
  const ref = XLSX.utils.encode_cell({ r, c });
  if (!ws[ref]) ws[ref] = { t: 's', v: '' };
  ws[ref].s = { ...(ws[ref].s || {}), ...style };
};

const setCell = (ws, r, c, value, style = {}, type) => {
  const ref = XLSX.utils.encode_cell({ r, c });
  const isNum = typeof value === 'number' && Number.isFinite(value);
  ws[ref] = {
    t: type || (isNum ? 'n' : 's'),
    v: value == null ? '' : value,
    s: style,
  };
  if (isNum && style.numFmt) {
    ws[ref].z = style.numFmt;
  }
};

const ensureRange = (ws, maxR, maxC) => {
  ws['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: maxR, c: maxC },
  });
};

/** Ortak üst bilgi bloğu (her sheet'te) */
const writeEventBanner = (ws, meta, startRow = 0) => {
  const {
    eventName,
    eventDate,
    sessionTime,
    eventId,
    stockId,
    city,
    venueName,
    exportedAt,
    selectedLabels = [],
  } = meta;

  setCell(ws, startRow, 0, 'Gişe Kıbrıs — Sale Report', {
    font: TITLE_FONT,
  });
  setCell(ws, startRow + 1, 0, eventName || '—', {
    font: { bold: true, sz: 13, name: 'Calibri' },
  });

  const metaRows = [
    ['Etkinlik Tarihi', eventDate || '—'],
    ['Seans / Saat', sessionTime || '—'],
    ['Şehir', city || '—'],
    ['Venue / Mekan', venueName || '—'],
    ['Event ID', eventId || '—'],
    ['Stock ID', stockId || '—'],
    ['Rapor Tarihi', exportedAt || '—'],
  ];

  metaRows.forEach(([label, value], i) => {
    const r = startRow + 3 + i;
    setCell(ws, r, 0, label, { font: META_LABEL_FONT });
    setCell(ws, r, 1, value, { font: META_VALUE_FONT });
  });

  const selRow = startRow + 3 + metaRows.length + 1;
  setCell(ws, selRow, 0, 'Seçilen Bölümler', { font: META_LABEL_FONT });
  setCell(
    ws,
    selRow,
    1,
    selectedLabels.length ? selectedLabels.join('  ·  ') : '—',
    { font: META_VALUE_FONT },
  );

  return selRow + 2; // data başlık satırı buradan
};

const applyHeaderRow = (ws, rowIndex, colCount) => {
  for (let c = 0; c < colCount; c += 1) {
    styleCell(ws, rowIndex, c, {
      font: HEADER_FONT,
      fill: HEADER_FILL,
      border: THIN_BORDER,
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    });
  }
};

const applyDataBorders = (ws, startRow, endRow, colCount, totalRowIndexes = new Set()) => {
  for (let r = startRow; r <= endRow; r += 1) {
    for (let c = 0; c < colCount; c += 1) {
      const isTotal = totalRowIndexes.has(r);
      styleCell(ws, r, c, {
        border: THIN_BORDER,
        font: isTotal ? TOTAL_FONT : { sz: 10, name: 'Calibri' },
        fill: isTotal ? TOTAL_FILL : undefined,
        alignment: { vertical: 'center' },
      });
    }
  }
};

const setColWidths = (ws, widths) => {
  ws['!cols'] = widths.map((wch) => ({ wch }));
};

const withoutTotalRows = (rows) =>
  (Array.isArray(rows) ? rows : []).filter((r) => r && r.id !== 'total');

const moneyHeaders = new Set([
  'Price',
  'Total Sale',
  'Discount (TL)',
  'Total Discount (TL)',
  'Down Payment',
  'Total Remaining',
  'Tutar',
]);

const appendTableSheet = (wb, sheetName, headers, tableRows, meta, emptyMessage = 'Kayıt yok') => {
  const ws = {};
  const dataStart = writeEventBanner(ws, meta, 0);

  setCell(ws, dataStart, 0, sheetName, { font: SECTION_FONT });

  const headerRow = dataStart + 2;
  headers.forEach((h, c) => {
    setCell(ws, headerRow, c, h, {
      font: HEADER_FONT,
      fill: HEADER_FILL,
      border: THIN_BORDER,
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    });
  });

  const rows = tableRows.length > 0 ? tableRows : [headers.map(() => '')];
  if (tableRows.length === 0) {
    setCell(ws, headerRow + 1, 0, emptyMessage, { font: META_VALUE_FONT });
  }

  const totalRowIndexes = new Set();
  rows.forEach((row, i) => {
    const r = headerRow + 1 + i;
    const isTotal =
      String(row[0] || '')
        .toUpperCase()
        .includes('TOPLAM') ||
      String(row[0] || '')
        .toUpperCase()
        .includes('TOTAL');
    if (isTotal) totalRowIndexes.add(r);

    headers.forEach((h, c) => {
      const val = row[c];
      const isMoney = moneyHeaders.has(h) && typeof val === 'number';
      setCell(
        ws,
        r,
        c,
        val == null ? '' : val,
        {
          border: THIN_BORDER,
          font: isTotal ? TOTAL_FONT : { sz: 10, name: 'Calibri' },
          fill: isTotal ? TOTAL_FILL : undefined,
          numFmt: isMoney ? '#,##0.00' : undefined,
          alignment: {
            vertical: 'center',
            horizontal: isMoney || typeof val === 'number' ? 'right' : 'left',
          },
        },
        isMoney || typeof val === 'number' ? 'n' : 's',
      );
    });
  });

  const lastRow =
    tableRows.length === 0 ? headerRow + 1 : headerRow + tableRows.length;
  ensureRange(ws, Math.max(lastRow, dataStart + 12), Math.max(headers.length - 1, 3));
  applyHeaderRow(ws, headerRow, headers.length);

  const widths = headers.map((h) => Math.min(28, Math.max(12, String(h).length + 4)));
  // widen first few cols
  if (widths[0]) widths[0] = Math.max(widths[0], 22);
  if (widths[1]) widths[1] = Math.max(widths[1], 24);
  setColWidths(ws, widths);

  ws['!rows'] = [{ hpt: 24 }];
  ws['!freeze'] = { xSplit: 0, ySplit: headerRow + 1, topLeftCell: XLSX.utils.encode_cell({ r: headerRow + 1, c: 0 }), activePane: 'bottomLeft', state: 'frozen' };

  XLSX.utils.book_append_sheet(wb, ws, uniqueSheetName(wb, sheetName));
};

const buildSalesAoA = (salesGrid) => {
  const headers = [
    'Product ID',
    'Title',
    'Category',
    'Price',
    'Total Stock',
    'Sold',
    'Remaining',
    'Total Sale',
  ];
  const rows = withoutTotalRows(salesGrid).map((r) => [
    r.id,
    r.title || '',
    r.category || '',
    moneyNum(r.price),
    r.stock ?? '',
    Number(r.sold || 0),
    r.remaining ?? '',
    moneyNum(r.totalSale ?? Number(r.sold || 0) * Number(r.price || 0)),
  ]);
  const totalSold = rows.reduce((s, row) => s + Number(row[5] || 0), 0);
  const totalSale = rows.reduce((s, row) => s + Number(row[7] || 0), 0);
  if (rows.length) {
    rows.push(['TOPLAM', '', '', '', '', totalSold, '', totalSale]);
  }
  return { headers, rows };
};

const buildCouponsAoA = (couponsGrid) => {
  const headers = [
    'Coupon Code',
    'Discount (TL)',
    'Stock',
    'Used',
    'Remaining',
    'Total Discount (TL)',
  ];
  const rows = withoutTotalRows(couponsGrid).map((r) => [
    r.code || '',
    moneyNum(r.discount),
    r.stock ?? '',
    r.used ?? '',
    r.stock != null && r.used != null ? Number(r.stock) - Number(r.used) : '',
    moneyNum(r.totalDiscount ?? Number(r.discount || 0) * Number(r.used || 0)),
  ]);
  const totalDisc = rows.reduce((s, row) => s + Number(row[5] || 0), 0);
  if (rows.length) {
    rows.push(['TOPLAM', '', '', '', '', totalDisc]);
  }
  return { headers, rows };
};

const buildRemainingAoA = (remainingGrid) => {
  const headers = [
    'Title',
    'Category',
    'Price',
    'Total Stock',
    'Sold',
    'Down Payment',
    'Total Remaining',
  ];
  const rows = withoutTotalRows(remainingGrid).map((r) => [
    r.title || '',
    r.category || '',
    moneyNum(r.price),
    r.stock ?? '',
    Number(r.sold || 0),
    moneyNum(r.downPaymentAmount),
    moneyNum(
      r.totalRmainingAmount ??
        (Number(r.price || 0) - Number(r.downPaymentAmount || 0)) * Number(r.sold || 0),
    ),
  ]);
  const totalRem = rows.reduce((s, row) => s + Number(row[6] || 0), 0);
  if (rows.length) {
    rows.push(['TOPLAM', '', '', '', '', '', totalRem]);
  }
  return { headers, rows };
};

const buildOwnerAoA = (owner) => {
  const headers = ['Kalem', 'Satıcı', 'User', 'Email', 'Telefon', 'Adet', 'Tutar'];
  const rows = [];
  (owner.lines || []).forEach((line) => {
    const sellers = Array.isArray(line.sellers) && line.sellers.length > 0 ? line.sellers : null;
    if (sellers) {
      sellers.forEach((s) => {
        rows.push([
          line.label,
          s.name || s.id || '',
          s.userName || s.userId || '',
          s.userEmail || '',
          s.userMobile || '',
          Number(s.count || 0),
          moneyNum(s.amount),
        ]);
      });
    } else if ((line.count || 0) > 0 || (line.amount || 0) > 0) {
      rows.push([
        line.label,
        '',
        '',
        '',
        '',
        Number(line.count || 0),
        moneyNum(line.amount),
      ]);
    }
  });
  rows.push([
    'TOPLAM',
    '',
    '',
    '',
    '',
    (owner.lines || []).reduce((sum, l) => sum + (l.count || 0), 0),
    moneyNum(owner.total),
  ]);
  return { headers, rows };
};

const buildSummaryAoA = (event, totals) => {
  const headers = ['Kalem', 'Tutar (TRY)'];
  const rows = [
    ['Total Revenue', moneyNum(totals.totalRevenue)],
    ['Total Product Sales', moneyNum(totals.totalProductSales)],
    ['Total Coupon Discount', moneyNum(totals.totalCouponDiscount)],
    ['Total Remaining Amount', moneyNum(totals.totalRemainingAmount)],
    ['Organisator Sales (Cash)', moneyNum(totals.orgVenueSales)],
    ['Sub Total', moneyNum(totals.subTotal)],
    [
      `Gişe Fee (${event?.isCommissionExtra ? 'extra' : 'included'} ${event?.commissionFee ?? 0}%)`,
      moneyNum(totals.totalCommissionAmount),
    ],
    ['Settlement Net', moneyNum(totals.settlementNet)],
    ['Total Payable', moneyNum(totals.totalPayable)],
    ['Ödenen', moneyNum(event?.paidAmount)],
  ];
  return { headers, rows };
};

const buildCoverSheet = (wb, meta) => {
  const ws = {};
  const {
    eventName,
    eventDate,
    sessionTime,
    eventId,
    stockId,
    city,
    venueName,
    exportedAt,
    selectedLabels = [],
  } = meta;

  setCell(ws, 0, 0, 'GİŞE KIBRIS', { font: TITLE_FONT });
  setCell(ws, 1, 0, 'Sale Report Excel Çıktısı', {
    font: { bold: true, sz: 14, color: { rgb: '1565C0' }, name: 'Calibri' },
  });

  setCell(ws, 3, 0, 'ETKİNLİK BİLGİLERİ', {
    font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' }, name: 'Calibri' },
    fill: HEADER_FILL,
  });
  setCell(ws, 3, 1, '', { fill: HEADER_FILL });

  const info = [
    ['Etkinlik Adı', eventName || '—'],
    ['Etkinlik Tarihi', eventDate || '—'],
    ['Seans / Saat', sessionTime || '—'],
    ['Şehir', city || '—'],
    ['Venue / Mekan', venueName || '—'],
    ['Event ID', eventId || '—'],
    ['Stock ID', stockId || '—'],
    ['Rapor Oluşturma', exportedAt || '—'],
  ];

  info.forEach(([label, value], i) => {
    const r = 4 + i;
    setCell(ws, r, 0, label, {
      font: META_LABEL_FONT,
      fill: { fgColor: { rgb: 'F5F5F5' } },
      border: THIN_BORDER,
    });
    setCell(ws, r, 1, value, {
      font: META_VALUE_FONT,
      border: THIN_BORDER,
    });
  });

  const secTitleRow = 4 + info.length + 1;
  setCell(ws, secTitleRow, 0, 'SEÇİLEN BÖLÜMLER', {
    font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' }, name: 'Calibri' },
    fill: HEADER_FILL,
  });
  setCell(ws, secTitleRow, 1, '', { fill: HEADER_FILL });

  if (!selectedLabels.length) {
    setCell(ws, secTitleRow + 1, 0, '—', { font: META_VALUE_FONT, border: THIN_BORDER });
    setCell(ws, secTitleRow + 1, 1, '', { border: THIN_BORDER });
  } else {
    selectedLabels.forEach((label, i) => {
      const r = secTitleRow + 1 + i;
      setCell(ws, r, 0, `${i + 1}.`, {
        font: META_LABEL_FONT,
        border: THIN_BORDER,
        alignment: { horizontal: 'center' },
      });
      setCell(ws, r, 1, label, {
        font: META_VALUE_FONT,
        border: THIN_BORDER,
      });
    });
  }

  const noteRow = secTitleRow + 2 + Math.max(selectedLabels.length, 1);
  setCell(
    ws,
    noteRow,
    0,
    'Not: Her seçilen bölüm ayrı Excel sayfasında (sheet) bulunur. Bu kapak sayfasında rapor özeti vardır.',
    { font: { sz: 9, italic: true, color: { rgb: '757575' }, name: 'Calibri' } },
  );

  ensureRange(ws, noteRow + 1, 3);
  setColWidths(ws, [28, 48, 16, 16]);
  XLSX.utils.book_append_sheet(wb, ws, 'Kapak');
};

/**
 * Sale report bölümleri için seçenek listesi (checkbox).
 */
export const buildSaleReportSectionOptions = (ownershipBreakdown) => {
  const options = [
    { id: 'sales', label: 'Sales' },
    { id: 'coupons', label: 'Coupons' },
    { id: 'remaining', label: 'Remaining Payments' },
  ];

  (ownershipBreakdown?.owners || []).forEach((owner) => {
    options.push({
      id: `owner:${owner.type}:${owner.id}`,
      label:
        owner.type === 'venue'
          ? `Venue: ${owner.name}`
          : `Organisation Company: ${owner.name}`,
      owner,
    });
  });

  options.push({ id: 'gise', label: 'Gişe Kıbrıs' });
  options.push({ id: 'summary', label: 'Summary' });
  return options;
};

/**
 * Seçilen bölümlere göre stilli Excel dosyası indirir.
 */
export const exportSaleReportExcel = ({
  event,
  stock,
  selectedIds,
  selectedLabels,
  salesGrid,
  couponsGrid,
  remainingGrid,
  ownershipBreakdown,
  totals,
  venueName,
}) => {
  const selected = new Set(selectedIds || []);
  if (selected.size === 0) {
    throw new Error('En az bir bölüm seçmelisin.');
  }

  const options = buildSaleReportSectionOptions(ownershipBreakdown);
  const labels =
    Array.isArray(selectedLabels) && selectedLabels.length
      ? selectedLabels
      : options.filter((o) => selected.has(o.id)).map((o) => o.label);

  const meta = {
    eventName: event?.name || '—',
    eventDate: fmtDate(event?.startdate),
    sessionTime: stock?.time || '—',
    eventId: event?.id || event?.uid || '—',
    stockId: stock?.id || '—',
    city: event?.city || '—',
    venueName: venueName || event?.venueName || event?.venue || '—',
    exportedAt: moment().format('DD.MM.YYYY HH:mm:ss'),
    selectedLabels: labels,
  };

  const wb = XLSX.utils.book_new();
  buildCoverSheet(wb, meta);

  if (selected.has('sales')) {
    const { headers, rows } = buildSalesAoA(salesGrid);
    appendTableSheet(wb, 'Sales', headers, rows, meta);
  }
  if (selected.has('coupons')) {
    const { headers, rows } = buildCouponsAoA(couponsGrid);
    appendTableSheet(wb, 'Coupons', headers, rows, meta);
  }
  if (selected.has('remaining')) {
    const { headers, rows } = buildRemainingAoA(remainingGrid);
    appendTableSheet(wb, 'Remaining', headers, rows, meta);
  }

  [...selected]
    .filter((id) => id.startsWith('owner:'))
    .forEach((id) => {
      const parts = id.split(':');
      const type = parts[1];
      const ownerId = parts.slice(2).join(':');
      const owner = (ownershipBreakdown?.owners || []).find(
        (o) => o.type === type && String(o.id) === String(ownerId),
      );
      if (!owner) return;
      const sheetTitle =
        owner.type === 'venue' ? `Venue ${owner.name}` : `Org ${owner.name}`;
      const { headers, rows } = buildOwnerAoA(owner);
      appendTableSheet(wb, sheetTitle, headers, rows, meta);
    });

  if (selected.has('gise')) {
    const { headers, rows } = buildOwnerAoA({
      lines: ownershipBreakdown?.gise?.lines || [],
      total: ownershipBreakdown?.gise?.total || 0,
    });
    appendTableSheet(wb, 'Gise Kibris', headers, rows, meta);
  }

  if (selected.has('summary')) {
    const { headers, rows } = buildSummaryAoA(event, totals || {});
    appendTableSheet(wb, 'Summary', headers, rows, meta);
  }

  const safeName = String(event?.name || 'event')
    .replace(/[^\w\-]+/g, '_')
    .slice(0, 40);
  const fileName = `sale-report-${safeName}-${moment().format('YYYYMMDD-HHmm')}.xlsx`;
  XLSX.writeFile(wb, fileName);
  return fileName;
};
